import type { SupabaseClient } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useGetList, useUpdate, useUpdateMany } from "ra-core";
import { useCallback, useMemo } from "react";
import {
  defaultRealtimeClient,
  useRealtimeInvalidate,
} from "../realtime/useRealtimeInvalidate";

export type GavionNotification = {
  id: number;
  sales_id: number;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
};

const POLL_MS = 60_000;

type Client = (() => SupabaseClient) | null;

/**
 * `countClient` / `realtimeClient`: test seams (default: the app's Supabase client; null in the demo).
 * The unread badge comes from my_unread_notification_count (all of them, not only the 20 listed);
 * without Supabase it counts the loaded page.
 */
export function useNotifications({
  countClient = defaultRealtimeClient,
  realtimeClient = defaultRealtimeClient,
}: { countClient?: Client; realtimeClient?: Client } = {}) {
  const { identity } = useGetIdentity();
  const hasIdentity = identity?.id !== undefined && identity?.id !== null;
  const { data, isPending } = useGetList<GavionNotification>(
    "notifications",
    {
      filter: { sales_id: identity?.id },
      sort: { field: "created_at", order: "DESC" },
      pagination: { page: 1, perPage: 20 },
    },
    { enabled: hasIdentity, refetchInterval: POLL_MS },
  );
  // Realtime: a new notification refreshes the bell at once (the poll stays as a fallback).
  useRealtimeInvalidate(
    "notifications",
    "notifications",
    hasIdentity ? `sales_id=eq.${identity.id}` : undefined,
    hasIdentity ? realtimeClient : null,
  );
  // Same resource key: Realtime and mark-as-read invalidations refresh it too.
  const { data: serverUnread } = useQuery({
    queryKey: ["notifications", "unread_count", identity?.id],
    queryFn: async () => {
      const { data, error } = await countClient!().rpc(
        "my_unread_notification_count",
      );
      if (error) throw new Error(error.message);
      return Number(data ?? 0);
    },
    enabled: hasIdentity && countClient !== null,
    refetchInterval: POLL_MS,
  });
  const items = useMemo(() => data ?? [], [data]);
  const unread = useMemo(() => items.filter((n) => !n.read_at), [items]);
  const [updateMany] = useUpdateMany();
  const [update] = useUpdate();
  const queryClient = useQueryClient();
  const refreshCount = useCallback(
    () =>
      void queryClient.invalidateQueries({
        queryKey: ["notifications", "unread_count"],
      }),
    [queryClient],
  );

  const markAllRead = useCallback(() => {
    if (!unread.length) return;
    updateMany(
      "notifications",
      {
        ids: unread.map((n) => n.id),
        data: { read_at: new Date().toISOString() },
      },
      { mutationMode: "pessimistic", onSuccess: refreshCount },
    );
  }, [unread, updateMany, refreshCount]);

  const markRead = useCallback(
    (n: GavionNotification) => {
      if (n.read_at) return;
      update(
        "notifications",
        {
          id: n.id,
          data: { read_at: new Date().toISOString() },
          previousData: n,
        },
        { mutationMode: "pessimistic", onSuccess: refreshCount },
      );
    },
    [update, refreshCount],
  );

  return {
    items,
    unreadCount: serverUnread ?? unread.length,
    isPending,
    markAllRead,
    markRead,
  };
}
